import React, { useMemo } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import { Link } from "react-router-dom"
import _ from "lodash"

import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { useTranslation } from "providers/i18n"
import { useTourist } from "extensions/em-tourist/hooks/useTourist"
import { useExtension } from "hooks/extension/useExtension"
import { EXTENSION_NAME } from "extensions/em-tourist/constant"
import { getIcon } from "utils/icon"

import { VenueSelector } from "components/VenueSelector"
import { PageCard, PageCardContent } from "components/card"
import { MotionDiv } from "components/motions"
import { AppNavigation } from "containers/AppNavigation"
import { NavigationControl } from "components/kioskHorizontal/navigation"
import { ImageCard } from "components/ImageCard"
import { EmptyData } from "components/EmptyData"
import { ShopIcon } from "components/icons"
import { useVenueController } from "hooks/venue/useVenueController"

export const EmTouristKioskHPage = ({ Layout = Box }) => {
  const { t } = useTranslation()
  const { isMultiVenueProject, venues } = useVenue()
  const { locationVenue } = useGeoLocation()
  const { extensionConfig } = useExtension()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)

  const theme = useTheme()

  const touristConfig = useMemo(
    () =>
      extensionConfig?.[EXTENSION_NAME]?.options || {
        locations: [],
      },
    [extensionConfig]
  )
  const touristLocations = _.get(touristConfig, "locations")

  const { filterByVenueId, filteredTouristByVenue } = useTourist(
    touristLocations,
    venueFilter
  )

  const { styleOverrides: bannerStyleOverrides = {} } =
    theme.components[`AppBanner`] || {}
  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}
  const { root: bannerRoot } = bannerStyleOverrides
  const { wrapper, body, footer } = sideBarStyleOverrides

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.tourist) || <ShopIcon />,
    [theme]
  )

  const styleOverrides = theme.components[`EmTouristPage`]?.styleOverrides || {}
  const {
    pageCard,
    contentWrapper,
    gridContainer,
    gridItem,
    emptyDataWrapper,
    venueSelector,
  } = styleOverrides

  return (
    <Layout
      aside={
        <PageCard>
          <Box sx={wrapper}>
            <Box sx={bannerRoot}></Box>
            <Box sx={body}>
              <AppNavigation />
            </Box>
            <Box sx={footer}>
              <NavigationControl BackButtonProps={{ hidden: true }} />
            </Box>
          </Box>
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent>
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            sx={contentWrapper}
          >
            {filteredTouristByVenue?.length > 0 ? (
              <Box sx={gridContainer}>
                {filteredTouristByVenue?.map((data) => {
                  const { name, location, image, level_name } = data || {}
                  const levelText = t("Level Text", {
                    level: t(level_name),
                  })
                  return (
                    <ImageCard
                      key={data?.id}
                      component={Link}
                      sx={gridItem}
                      to={`/maps/place/${location?.id}`}
                      title={
                        <>
                          {t(name)}
                          <Typography
                            className="location-text"
                            component="span"
                            variant="subtitle"
                          >
                            {levelText}
                          </Typography>
                        </>
                      }
                      image={image}
                      avatar={null}
                    />
                  )
                })}
              </Box>
            ) : (
              <Box sx={emptyDataWrapper}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("Coming soon")}
                  secondary={t("Stay tuned for the update")}
                />
              </Box>
            )}
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={filterByVenueId}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
          </MotionDiv>
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
